/**
 * PROJECT SURFACE — the three things a learner PRODUCES, in one place.
 *
 * FOUNDER, 2026-08-15: "build p1" · "build p2 and p4" · then "where exactly can i see all this".
 *
 * ── THAT QUESTION FOUND A REAL FAULT ──────────────────────────────────────────────────────────
 * P1, P2 and P4 were built, gated and compiled into the bundle — and were reachable from NOWHERE.
 * Every lab in this app renders through LabSlot, which is keyed on (level, chapter) and allows one
 * lab per chapter. These three are not chapter labs: a project spans a whole ladder, an audit is of
 * a product rather than a chapter, and a certificate is the end of both. They had no home.
 *
 * I had reported them "live in the public bundle" and the string really was there — but presence in
 * a bundle is not reachability, and checking the proxy instead of the property is precisely what
 * this academy's own chapter 8 warns about. The founder asking where to click is what surfaced it.
 *
 * ── ONE SURFACE, IN THE ORDER THE WORK HAPPENS ────────────────────────────────────────────────
 * Project first — it is what the other two rest on. Then the audit, which needs somewhere to live.
 * Then the certificate, which can say nothing until one of them passes. A learner arriving here
 * before doing anything sees exactly what will be asked of them, which is the published-standard
 * law applied to the navigation as well as to the checks.
 */

import { useState } from 'react';
import ProjectLab from './projectlab.jsx';
import AuditLab from './auditlab.jsx';
import CertLab from './certlab.jsx';
import JournalLab from './journallab.jsx';
import MANIFEST from '../../project-manifest.json';
import { carriesProject } from './projectspine.mjs';

/* The ladders that declare a project. Read from the manifest, never hardcoded — a ladder added
   there appears here without touching this file. */
const LADDERS = (MANIFEST.ladders || []).filter((l) => l.carries_project === true).map((l) => l.level);

const PARTS = [
  { id: 'project', label: 'Your project', hint: 'One project, carried across the ladder' },
  { id: 'journal', label: 'Day by day', hint: 'The questions each day asks of your own project' },
  { id: 'audit', label: 'Audit a product', hint: 'A real ANKR product, against STATE/TRUST/SENSE/PROOF' },
  /* FOUNDER, 2026-08-27, on being asked where the doing layer lives: fold it in here.
     project.html was built the night before as a separate page at /project — the Scratch-style
     day-by-day journal — and it was reachable from NOWHERE in the shell, exactly the fault the
     comment at the top of this file records for P1/P2/P4. A second surface called Project is worse
     than a second URL: a candidate tapping Project got the certificate track while the journal he
     was told to keep sat at an address nobody gives him. One Project, four parts, in the order the
     work happens: the project, the journal that carries it day by day, the audit, the certificate. */
  { id: 'certificate', label: 'Certificate', hint: 'What you built, and the check that passed' },
];

export default function ProjectSurface() {
  const [part, setPart] = useState('project');
  const [level, setLevel] = useState(LADDERS[0] || 'CODEAI101');

  return (
    <div className="prs">
      <div className="lab-task-head prs-nav">
        {PARTS.map((p) => (
          <button key={p.id} className={`g-btn ${part === p.id ? 'g-btn-primary' : ''}`}
            title={p.hint} onClick={() => setPart(p.id)}>{p.label}</button>
        ))}
      </div>

      {/* The ladder chooser only appears where it changes something — the audit is of a product,
          not of a ladder, so showing a ladder switch there would imply a link that is not real. */}
      {part !== 'audit' && LADDERS.length > 1 && (
        <div className="lab-task-head prs-nav">
          <span className="g-label">Ladder</span>
          {LADDERS.map((l) => (
            <button key={l} className={`g-btn ${level === l ? 'g-btn-primary' : ''}`}
              onClick={() => setLevel(l)}>{l}</button>
          ))}
        </div>
      )}

      {part === 'project' && <ProjectLab level={level} />}
      {part === 'audit' && <AuditLab />}
      {part === 'journal' && <JournalLab level={level} />}
      {part === 'certificate' && <CertLab level={carriesProject(MANIFEST, level) ? level : LADDERS[0]} />}
    </div>
  );
}
